import { PROMETHEUS_SERVICE } from '../../bootstrap/cluster-diagnostics.js';
import { singleValue } from '../../cloud/prometheus.js';
import { count, listed, plural } from './format.js';
import { age, bytes, groupBy, hasItems, hoursBetween, insideOf, kindOf, quantity, } from './inside.js';
import { isEvaluation } from './model.js';
import { THRESHOLDS } from './thresholds.js';
/*
 * Checks over the cluster's own Prometheus (IM-207): what it holds, what it
 * scrapes, and what Kong answered. Each reads `ClusterState.prometheus`, which
 * the cluster reader fills through the one proxy the diagnostics role may use.
 *
 * A Prometheus that did not answer makes each of these `unknown`, with the
 * reader's reason and, where the pod is visible, when it last started — a
 * restart is the commonest reason it does not answer, and the start time says
 * whether this was one.
 */
const P = THRESHOLDS.prometheus;
/** The Prometheus the proxy reaches: the one in its service's namespace, else the first. */
const prometheusOf = (facts) => {
    const inside = insideOf(facts);
    if (isEvaluation(inside))
        return inside;
    const proms = kindOf(inside.value, 'prometheuses', 'the Prometheus resources');
    if (!hasItems(proms))
        return proms;
    const prom = proms.items.find((p) => p.namespace === PROMETHEUS_SERVICE.namespace) ?? proms.items[0];
    if (prom === undefined) {
        return { outcome: 'not-applicable', finding: 'This cluster runs no Prometheus.', evidence: [] };
    }
    const podsRead = inside.value.kinds.pods;
    const pods = podsRead.state === 'read'
        ? podsRead.items.filter((p) => p.namespace === prom.namespace && p.name.startsWith(`prometheus-${prom.name}-`))
        : [];
    const read = inside.value.prometheus;
    if (read.state === 'unreadable') {
        return {
            outcome: 'unknown',
            finding: 'Could not read the cluster’s Prometheus.',
            evidence: [
                { label: 'Reason', value: read.reason },
                ...pods.map((pod) => ({
                    label: `Pod ${pod.name}`,
                    value: startedNote(pod, facts.at),
                })),
            ],
        };
    }
    return { cluster: inside.value, prom, pods, state: read.value };
};
const isFinding = (value) => 'outcome' in value;
const startedNote = (pod, at) => {
    const since = pod.startedAt ?? pod.createdAt;
    const restarts = pod.containers.find((c) => c.name === 'prometheus')?.restartCount;
    return [
        pod.phase ?? 'phase not reported',
        since === undefined ? undefined : `started ${age(hoursBetween(since, at))} ago`,
        restarts === undefined || restarts === 0 ? undefined : plural(restarts, 'restart'),
    ]
        .filter((part) => part !== undefined)
        .join(', ');
};
/** A query's answers, or the evaluation that says it could not be read. */
const answersOf = (state, id, what) => {
    const read = state.queries[id];
    return read.state === 'read'
        ? { samples: read.samples }
        : {
            outcome: 'unknown',
            finding: `Prometheus did not answer ${what}.`,
            evidence: [{ label: 'Reason', value: read.reason }],
        };
};
const isAnswered = (value) => 'samples' in value;
/**
 * Whether Prometheus holds more series than its memory allows (IM-207).
 *
 * Its memory grows with its head series, so its use per series now, times the
 * most series it held in the last day, is where it is going. Past its own
 * limit it is killed; past its node's memory it is evicted first, which is
 * what happened on 24/7 NV with 2.1 million series on an 8 GiB node. The
 * largest metrics are the evidence, because cutting what is scraped is
 * usually the fix and a larger node only moves the ceiling.
 *
 * `prometheus-fit` judges the memory it uses now and the evictions the
 * cluster still remembers; this judges where the series take it.
 */
export const prometheusSeriesCheck = {
    id: 'prometheus-series',
    title: 'Prometheus holds what its memory allows',
    category: 'observability',
    outcomes: ['ok', 'warn', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const found = prometheusOf(facts);
        if (isFinding(found))
            return found;
        const { cluster, prom, pods, state } = found;
        const tsdb = state.tsdb.state === 'read' ? state.tsdb : undefined;
        const series = singleValue(state.queries.headSeries) ?? tsdb?.series;
        if (series === undefined || series <= 0) {
            return {
                outcome: 'unknown',
                finding: 'Prometheus did not say how many series it holds.',
                evidence: state.queries.headSeries.state === 'unreadable'
                    ? [{ label: 'Reason', value: state.queries.headSeries.reason }]
                    : [],
            };
        }
        const peak = Math.max(series, singleValue(state.queries.headSeriesPeak) ?? series);
        const evidence = [
            { label: 'Series now', value: count(series) },
            { label: 'Most in the last day', value: count(peak) },
            ...(tsdb === undefined || tsdb.topMetrics.length === 0
                ? []
                : [
                    {
                        label: 'Largest metrics',
                        value: listed(tsdb.topMetrics.slice(0, 5).map((m) => `${m.name} (${count(m.value)})`)),
                    },
                ]),
            ...(tsdb === undefined || tsdb.topLabelPairs.length === 0
                ? []
                : [
                    {
                        label: 'Largest label values',
                        value: listed(tsdb.topLabelPairs.slice(0, 3).map((m) => `${m.name} (${count(m.value)})`)),
                    },
                ]),
        ];
        const metrics = cluster.kinds.podMetrics.state === 'read' ? cluster.kinds.podMetrics.items : [];
        const nodes = cluster.kinds.nodes.state === 'read' ? cluster.kinds.nodes.items : [];
        const pod = pods[0];
        const used = metrics
            .find((m) => pod !== undefined && m.namespace === pod.namespace && m.name === pod.name)
            ?.containers.filter((c) => c.name === 'prometheus')
            .reduce((n, c) => n + (quantity(c.memory) ?? 0), 0);
        const limit = quantity(prom.limits['memory']);
        const node = nodes.find((n) => n.name === pod?.node);
        const allocatable = quantity(node?.allocatable['memory']);
        if (used === undefined || used === 0) {
            return {
                outcome: 'unknown',
                finding: 'Prometheus’s memory use was not reported, so where its series take it cannot be worked out.',
                evidence,
            };
        }
        const projected = (used / series) * peak;
        const ceilings = [
            ...(limit === undefined ? [] : [{ what: 'its own limit', bytes: limit }]),
            ...(allocatable === undefined
                ? []
                : [{ what: `its node’s memory (${node?.instanceType ?? 'node'})`, bytes: allocatable }]),
        ].sort((a, b) => a.bytes - b.bytes);
        evidence.push({
            label: 'Memory now',
            value: `${bytes(used)}, about ${String(Math.round(used / series))} bytes a series`,
        }, { label: 'At the day’s peak', value: `about ${bytes(projected)}` }, ...ceilings.map((c) => ({ label: `Ceiling: ${c.what}`, value: bytes(c.bytes) })));
        const ceiling = ceilings[0];
        if (ceiling === undefined) {
            return {
                outcome: 'unknown',
                finding: 'Prometheus has no memory limit and its node is not known, so there is no ceiling to judge its series against.',
                evidence,
            };
        }
        if (projected >= ceiling.bytes) {
            return {
                outcome: 'warn',
                finding: `At the day’s peak of ${count(peak)} series Prometheus would use about ${bytes(projected)}, past ${ceiling.what} of ${bytes(ceiling.bytes)}.`,
                recommendation: 'Cut the series it holds — the largest metrics are listed — or give it a node and a limit above that peak. A Prometheus that reaches its node’s memory is evicted and loses everything since its last block.',
                evidence,
            };
        }
        return {
            outcome: 'ok',
            finding: `Prometheus holds ${count(series)} series; at the day’s peak that is about ${bytes(projected)}, within ${ceiling.what} of ${bytes(ceiling.bytes)}.`,
            evidence,
        };
    },
};
/** kube-prometheus-stack's job for the kubelet, whose cAdvisor endpoint is every container metric. */
const KUBELET_JOB = 'kubelet';
/**
 * Whether Prometheus's scrape targets answer (IM-207).
 *
 * A target that is down is a gap in every metric it carries, and the kubelet's
 * is the one everything else depends on: its cAdvisor endpoint is where
 * container memory, CPU and restarts come from. So a kubelet that is down, or
 * not scraped at all, fails; anything else down warns.
 */
export const scrapeTargetsCheck = {
    id: 'scrape-targets',
    title: 'Prometheus scrapes every target',
    category: 'observability',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const found = prometheusOf(facts);
        if (isFinding(found))
            return found;
        const byJob = answersOf(found.state, 'targetsByJob', 'which targets it scrapes');
        if (!isAnswered(byJob))
            return byJob;
        const down = answersOf(found.state, 'targetsDown', 'which targets are down');
        if (!isAnswered(down))
            return down;
        const totals = new Map(byJob.samples.map((s) => [s.labels.job ?? '', s.value]));
        const total = [...totals.values()].reduce((n, v) => n + v, 0);
        const downByJob = groupBy(down.samples, (s) => s.labels.job ?? 'unnamed job');
        const evidence = [...downByJob].map(([job, targets]) => ({
            label: `Down: ${job}`,
            value: `${String(targets.length)} of ${String(totals.get(job) ?? targets.length)}${targets.length === 0
                ? ''
                : ` (${listed(targets
                    .slice(0, 3)
                    .map((t) => [t.labels.instance, t.labels.metrics_path].filter(Boolean).join(' ')))})`}`,
        }));
        evidence.push({ label: 'Jobs scraped', value: listed([...totals.keys()].sort()) });
        if (!totals.has(KUBELET_JOB)) {
            return {
                outcome: 'fail',
                finding: 'Prometheus scrapes no kubelet, so it records no container metrics: memory, CPU and restarts per container are missing for every workload.',
                recommendation: 'Check that the kubelet ServiceMonitor exists and selects the kubelet service, and that Prometheus selects that ServiceMonitor.',
                evidence,
            };
        }
        const kubeletDown = downByJob.get(KUBELET_JOB)?.length ?? 0;
        if (kubeletDown > 0) {
            return {
                outcome: 'fail',
                finding: `${plural(kubeletDown, 'kubelet target')} did not answer Prometheus, so container metrics are missing for ${kubeletDown === 1 ? 'that node' : 'those nodes'}.`,
                recommendation: 'Look at the nodes named: a kubelet Prometheus cannot reach is usually a node security group that does not admit the monitoring node on 10250, or a node that is going away.',
                evidence,
            };
        }
        if (down.samples.length > 0) {
            return {
                outcome: 'warn',
                finding: `${plural(down.samples.length, 'scrape target')} of ${count(total)} did not answer: ${listed([...downByJob.keys()])}.`,
                recommendation: 'A target down for one scrape is a restart; one down for long is a pod that is gone or a port nothing listens on. The metrics it carries are missing until it answers.',
                evidence,
            };
        }
        return {
            outcome: 'ok',
            finding: `All ${count(total)} scrape targets answered, the kubelet’s included.`,
            evidence,
        };
    },
};
/** A ratio as a percentage a person can compare: one decimal, and never "0%" for some. */
const share = (ratio) => ratio > 0 && ratio < 0.001 ? 'under 0.1%' : `${(Math.round(ratio * 1000) / 10).toFixed(1)}%`;
/**
 * Whether Kong answers without server errors (IM-207).
 *
 * Kong is where every request to the instance arrives, so its 5xx share is
 * what users see. Judged over the last hour and the last day, because a bad
 * hour and a bad day send someone to different places, and only past
 * `kongMinRequests`: a ratio over a handful of requests is noise. The routes
 * that answered most of the errors are the evidence.
 */
export const kongErrorsCheck = {
    id: 'kong-errors',
    title: 'Kong answers without server errors',
    category: 'observability',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const found = prometheusOf(facts);
        if (isFinding(found))
            return found;
        const q = found.state.queries;
        const day = answersOf(found.state, 'kongRequestsDay', 'how many requests Kong answered');
        if (!isAnswered(day))
            return day;
        if (day.samples.length === 0) {
            return {
                outcome: 'not-applicable',
                finding: 'Prometheus holds no Kong request metrics, so there is nothing to judge.',
                evidence: [],
            };
        }
        const requestsDay = day.samples[0]?.value ?? 0;
        const errorsDay = singleValue(q.kongErrorsDay) ?? 0;
        const requestsHour = singleValue(q.kongRequestsHour) ?? 0;
        const errorsHour = singleValue(q.kongErrorsHour) ?? 0;
        const services = q.kongErrorServices.state === 'read' ? q.kongErrorServices.samples : [];
        const ratioHour = requestsHour >= P.kongMinRequests ? errorsHour / requestsHour : undefined;
        const ratioDay = requestsDay >= P.kongMinRequests ? errorsDay / requestsDay : undefined;
        const evidence = [
            {
                label: 'Last hour',
                value: `${count(errorsHour)} server errors of ${count(requestsHour)} requests${ratioHour === undefined ? '' : ` (${share(ratioHour)})`}`,
            },
            {
                label: 'Last day',
                value: `${count(errorsDay)} server errors of ${count(requestsDay)} requests${ratioDay === undefined ? '' : ` (${share(ratioDay)})`}`,
            },
            ...(services.length === 0
                ? []
                : [
                    {
                        label: 'Most errors, last day',
                        value: listed(services.map((s) => `${s.labels.service ?? 'unnamed'} (${count(s.value)})`)),
                    },
                ]),
        ];
        if (ratioHour === undefined && ratioDay === undefined) {
            return {
                outcome: 'ok',
                finding: `Kong answered ${count(requestsDay)} requests in the last day, too few to judge as a share.`,
                evidence,
            };
        }
        const recommendation = 'Start with the routes listed: a 502 or 503 is Kong finding no ready pod behind the route, a 500 the service itself failing. Their pods’ restarts and events say which.';
        if (ratioHour !== undefined && ratioHour >= P.kongErrorFailRatio) {
            return {
                outcome: 'fail',
                finding: `${share(ratioHour)} of Kong’s answers in the last hour were server errors.`,
                recommendation,
                evidence,
            };
        }
        const worse = Math.max(ratioHour ?? 0, ratioDay ?? 0);
        if (worse >= P.kongErrorWarnRatio) {
            const window = (ratioHour ?? 0) >= (ratioDay ?? 0) ? 'hour' : 'day';
            return {
                outcome: 'warn',
                finding: `${share(worse)} of Kong’s answers in the last ${window} were server errors.`,
                recommendation,
                evidence,
            };
        }
        return {
            outcome: 'ok',
            finding: `Kong’s server errors were ${share(ratioDay ?? ratioHour ?? 0)} of its answers in the last ${ratioDay === undefined ? 'hour' : 'day'}.`,
            evidence,
        };
    },
};
//# sourceMappingURL=metrics.js.map